// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useQuery } from '@tanstack/react-query';
import { Template } from '@daml/types';
import { useLedgerApiClient } from '../../contexts/LedgerApiContext';
import { HistoryPage } from '../../utils/LedgerApiClientV2';
import TemplateContract from '../../utils/TemplateContract';
import { useLedgerEnd } from './ledger/useLedgerEnd';

const PAGE_SIZE = 100;

export interface TemplateHistory<T extends object> {
  data: TemplateContract<T>[];
  isLoading: boolean;
}

export function useTemplateHistory<T extends object, K, I extends string>(
  template: Template<T, K, I>,
  party?: string,
): TemplateHistory<T> {
  const ledgerApi = useLedgerApiClient();
  const offset = useLedgerEnd();

  const query = useQuery<TemplateContract<T>[], string>({
    queryKey: [template, offset, party],
    queryFn: async ({ signal }) => {
      const contracts: TemplateContract<T>[] = [];
      let pageToken: string | undefined;
      do {
        const page: HistoryPage<T> = await ledgerApi.queryHistory(template, {
          endOffset: offset!,
          pageSize: PAGE_SIZE,
          pageToken,
          party,
        });
        contracts.push(...page.data);
        pageToken = page.nextPageToken || undefined;
      } while (pageToken && !signal?.aborted);
      return contracts;
    },
    enabled: !!offset,
    refetchInterval: false,
  });

  const data = query.data ?? [];
  const isLoading = !offset || query.isLoading;

  return { data, isLoading };
}
